import SubHeaders from '@/components/Headers/SubHeaders'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useTabs from '@/hooks/useTabs'
import { paths } from '@/utils/path'
import { useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Loading, Tabs } from 'vnetwork-ui'
import CancellationRateChart from './components/CancellationRateChart'
import EmployeeReport from './components/EmployeeReport'
import Filters from './components/Filters'
import MonthlyDataTable from './components/MonthlyDataTable'
import ProductGroupReport from './components/ProductGroupReport'
import ServiceAnalysis from './components/ServiceAnalysis'
import ServicesTrendChart from './components/ServicesTrendChart'
import SummaryCards from './components/SummaryCards'
import {
    getEmployeeReport,
    getProductGroupReport,
    getServiceMonthlyReport,
} from './services'
import { ServiceMonthlyReportFilters } from './types'

interface FormValues extends ServiceMonthlyReportFilters {
    showComparison: boolean
}

const ServiceMonthlyReport = () => {
    const { t } = useTranslation()
    const currentYear = new Date().getFullYear()

    const { control, setValue } = useForm<FormValues>({
        defaultValues: {
            year: currentYear,
            product_group_name: undefined,
            account_manager_codes: undefined,
            sale_manager_codes: undefined,
            showComparison: true,
        },
    })

    const year = useWatch({ control, name: 'year' })
    const productGroupName = useWatch({ control, name: 'product_group_name' })
    const accountManagerCodes = useWatch({
        control,
        name: 'account_manager_codes',
    })
    const saleManagerCodes = useWatch({ control, name: 'sale_manager_codes' })
    const showComparison = useWatch({ control, name: 'showComparison' })

    // Date range for product group and employee reports
    const dateRange = useMemo(() => {
        return {
            from_date: `${year}-01-01`,
            to_date: `${year}-12-31`,
        }
    }, [year])

    // Previous year date range
    const prevDateRange = useMemo(() => {
        return {
            from_date: `${year - 1}-01-01`,
            to_date: `${year - 1}-12-31`,
        }
    }, [year])

    // Fetch current year data
    const {
        data: currentYearData,
        renderState,
        isLoading,
    } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.REPORTS_BI_SERVICE_MONTHLY,
                year,
                productGroupName,
                accountManagerCodes,
                saleManagerCodes,
            ],
            queryFn: () =>
                getServiceMonthlyReport({
                    year,
                    product_group_name: productGroupName,
                    account_manager_codes: accountManagerCodes,
                    sale_manager_codes: saleManagerCodes,
                }),
            enabled: !!year,
        },
        {
            loadingComponent: (
                <div className="flex justify-center items-center h-[400px]">
                    <Loading.CircularProgress width={40} height={40} />
                </div>
            ),
        }
    )

    // Fetch previous year data for comparison
    const { data: previousYearData } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.REPORTS_BI_SERVICE_MONTHLY,
                year - 1,
                productGroupName,
                accountManagerCodes,
                saleManagerCodes,
            ],
            queryFn: () =>
                getServiceMonthlyReport({
                    year: year - 1,
                    product_group_name: productGroupName,
                    account_manager_codes: accountManagerCodes,
                    sale_manager_codes: saleManagerCodes,
                }),
            enabled: !!year && showComparison,
        },
        {}
    )

    // Fetch product group report
    const { data: productGroupData } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.REPORTS_BI_SERVICE_MONTHLY,
                'product-group',
                dateRange.from_date,
                dateRange.to_date,
                accountManagerCodes,
                saleManagerCodes,
            ],
            queryFn: () =>
                getProductGroupReport({
                    ...dateRange,
                    account_manager_codes: accountManagerCodes,
                    sale_manager_codes: saleManagerCodes,
                }),
            enabled: !!year,
        },
        {}
    )

    // Fetch previous year product group report for comparison
    const { data: prevProductGroupData } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.REPORTS_BI_SERVICE_MONTHLY,
                'product-group',
                prevDateRange.from_date,
                prevDateRange.to_date,
                accountManagerCodes,
                saleManagerCodes,
            ],
            queryFn: () =>
                getProductGroupReport({
                    ...prevDateRange,
                    account_manager_codes: accountManagerCodes,
                    sale_manager_codes: saleManagerCodes,
                }),
            enabled: !!year && showComparison,
        },
        {}
    )

    // Fetch Account Manager report
    const { data: accountManagerData } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.REPORTS_BI_SERVICE_MONTHLY,
                'employee',
                'account_manager',
                dateRange.from_date,
                dateRange.to_date,
                accountManagerCodes,
                saleManagerCodes,
            ],
            queryFn: () =>
                getEmployeeReport({
                    ...dateRange,
                    employee_type: 'account_manager',
                    account_manager_codes: accountManagerCodes,
                    sale_manager_codes: saleManagerCodes,
                }),
            enabled: !!year,
        },
        {}
    )

    // Fetch Sale Manager report
    const { data: saleManagerData } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.REPORTS_BI_SERVICE_MONTHLY,
                'employee',
                'sale_manager',
                dateRange.from_date,
                dateRange.to_date,
                accountManagerCodes,
                saleManagerCodes,
            ],
            queryFn: () =>
                getEmployeeReport({
                    ...dateRange,
                    employee_type: 'sale_manager',
                    account_manager_codes: accountManagerCodes,
                    sale_manager_codes: saleManagerCodes,
                }),
            enabled: !!year,
        },
        {}
    )

    const tabs = useMemo(
        () => [
            {
                label: t('monthly_report'),
                value: 'monthly',
                component: currentYearData ? (
                    <div className="space-y-2">
                        <SummaryCards
                            data={currentYearData}
                            previousYearData={previousYearData}
                            showComparison={showComparison}
                        />

                        {/* Charts Row */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                            {/* Services Trend Chart */}
                            <ServicesTrendChart
                                data={currentYearData}
                                previousYearData={previousYearData}
                                showComparison={showComparison}
                                year={year}
                            />

                            {/* Cancellation Rate Chart */}
                            <CancellationRateChart
                                data={currentYearData}
                                previousYearData={previousYearData}
                                showComparison={showComparison}
                                year={year}
                            />
                        </div>

                        {/* Monthly Data Table */}
                        <MonthlyDataTable
                            data={currentYearData}
                            previousYearData={previousYearData}
                            showComparison={showComparison}
                            year={year}
                            filters={{
                                product_group_name: productGroupName,
                                account_manager_codes: accountManagerCodes,
                                sale_manager_codes: saleManagerCodes,
                            }}
                        />
                    </div>
                ) : (
                    <></>
                ),
            },
            {
                label: t('reports.analysis.key_insights'),
                value: 'analysis',
                component: currentYearData ? (
                    <ServiceAnalysis
                        monthlyData={currentYearData}
                        previousYearMonthlyData={previousYearData}
                        productGroupData={productGroupData}
                        previousYearProductGroupData={prevProductGroupData}
                        year={year}
                        showComparison={showComparison}
                    />
                ) : (
                    <></>
                ),
            },
            {
                label: t('by_product_group'),
                value: 'product-group',
                component: productGroupData ? (
                    <ProductGroupReport
                        data={productGroupData}
                        previousYearData={prevProductGroupData}
                        showComparison={showComparison}
                        year={year}
                        fromDate={dateRange.from_date}
                        toDate={dateRange.to_date}
                    />
                ) : (
                    <></>
                ),
            },
            {
                label: t('by_account_manager'),
                value: 'account-manager',
                component: accountManagerData ? (
                    <EmployeeReport
                        data={accountManagerData}
                        employeeType="account_manager"
                        fromDate={dateRange.from_date}
                        toDate={dateRange.to_date}
                    />
                ) : (
                    <></>
                ),
            },
            {
                label: t('by_sale_manager'),
                value: 'sale-manager',
                component: saleManagerData ? (
                    <EmployeeReport
                        data={saleManagerData}
                        employeeType="sale_manager"
                        fromDate={dateRange.from_date}
                        toDate={dateRange.to_date}
                    />
                ) : (
                    <></>
                ),
            },
        ],
        [
            t,
            currentYearData,
            previousYearData,
            showComparison,
            year,
            productGroupData,
            prevProductGroupData,
            accountManagerData,
            saleManagerData,
            dateRange,
        ]
    )

    const { TabProps, TabComponent } = useTabs({ tabs })

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="space-y-2">
            <SubHeaders
                breadcrumbs={[
                    {
                        title: t('reports'),
                        path: '/reports',
                    },
                    {
                        title: t('service_monthly_report'),
                        path: paths.reportServiceMonthly,
                    },
                ]}
                actions={[]}
            />

            {/* Filters */}
            <Filters
                control={control}
                setValue={setValue}
                showComparison={showComparison}
            />

            {isLoading ? (
                <div className="flex justify-center items-center h-[400px]">
                    <Loading.CircularProgress width={40} height={40} />
                </div>
            ) : (
                <>
                    <Tabs {...TabProps} />
                    {TabComponent}
                </>
            )}
        </div>
    )
}

export default ServiceMonthlyReport
